
import * as React from 'react';
import { mask, unmask, Masks, CreateNumberMaskProps, CurrencyConfig } from '@sicredi/mask';
import { ValidatorCNPJ, ValidatorsCPF } from '@sicredi/validators';

import Input, { Props as InputProps } from '../input/Input';
import { normalizeNumber } from '../utils';

export interface Props extends InputProps {
  mask: Masks;
  preserveChars?: string[];
  config?: CreateNumberMaskProps | CurrencyConfig;
  validate?: boolean;
}

export interface CompoundedMaskedInput
  extends React.ForwardRefExoticComponent<
    Props & React.RefAttributes<HTMLInputElement>
  > {
  mask(str: any, mask: Masks, config?: {}): string;
  unmask(str: any, preserveChars?: string[]): string;
}

const customMaskCpfCnpj = (value: string): string => {
  const cleaned = value.replace(/[^0-9A-Z]/gi, '').toUpperCase();
  if (/[A-Z]/.test(cleaned)) {
    return mask(cleaned, 'cnpj');
  }
  if (cleaned.length <= 11) {
    return mask(cleaned, 'cpf');
  }
  return mask(cleaned, 'cnpj');
};

const MaskedInput = React.forwardRef<HTMLInputElement, Props>(
  (
    { mask: maskPattern, value, onChange, preserveChars, config, validate, ...props },
    ref
  ) => {
    const [internalValue, setInternalValue] = React.useState('');
    const [validationError, setValidationError] = React.useState<string | null>(null);

    const preserveCharsConfig =
      config && config['allowNegative']
        ? (preserveChars || []).concat('-', '+')
        : preserveChars;

    const unmaskFn = (str: any): string => {
      if (maskPattern === 'cnpj' || maskPattern === 'cpf_cnpj') {
        return String(str).replace(/[^0-9A-Z]/gi, '').toUpperCase();
      }
      return unmask(str, preserveCharsConfig);
    };

    const runValidation = React.useCallback((inputValue: string) => {
      if (!validate) {
        setValidationError(null);
        return;
      }

      switch (maskPattern) {
        case 'cnpj':
          if (!inputValue) {
            setValidationError(null);
          } else {
            const isValid = ValidatorCNPJ.isValid(inputValue);
            setValidationError(isValid ? null : 'CNPJ incorreto, tente novamente.');
          }
          break;
        case 'cpf_cnpj':
          if (!inputValue) {
            setValidationError(null);
          } else {
            const cleaned = inputValue.replace(/[^0-9A-Z]/gi, '').toUpperCase();
            const onlyNumbers = cleaned.replace(/\D/g, '');
            if (/[A-Z]/.test(cleaned) && cleaned.length === 14) {
              const isValidCNPJ = ValidatorCNPJ.isValid(cleaned);
              setValidationError(isValidCNPJ ? null : 'CNPJ incorreto, tente novamente.');
            } else if (onlyNumbers.length === 11) {
              const isValidCPF = ValidatorsCPF.isValid(onlyNumbers);
              setValidationError(isValidCPF ? null : 'CPF incorreto, tente novamente.');
            } else if (onlyNumbers.length === 14) {
              const isValidCNPJ = ValidatorCNPJ.isValid(onlyNumbers);
              setValidationError(isValidCNPJ ? null : 'CNPJ incorreto, tente novamente.');
            } else {
              setValidationError('Documento inválido, tente novamente.');
            }
          }
          break;
        default:
          setValidationError(null);
      }
    }, [validate, maskPattern]);

    function handleChange(e: React.ChangeEvent<HTMLInputElement>): void {
      const isWhiteSpace = internalValue[internalValue.length - 1] === ' ';
      const unmaskedValue = unmaskFn(e.target.value);
      const unmaskedInternalValue = unmaskFn(internalValue);

      const nextValue =
        unmaskedValue === unmaskedInternalValue
          ? e.target.value.slice(0, isWhiteSpace ? -2 : -1)
          : e.target.value;

      const maskedValue =
        maskPattern === 'CURRENCY'
          ? mask(normalizeNumber(unmaskFn(nextValue)), maskPattern, config)
          : maskPattern === 'cpf_cnpj'
            ? customMaskCpfCnpj(nextValue)
            : mask(unmaskFn(nextValue), maskPattern, config);

      e.target.value = maskedValue;

      setInternalValue(maskedValue);

      onChange && onChange(e);
    }

    function handleBlur(e: React.FocusEvent<HTMLInputElement>): void {
      if (validate) {
        runValidation(e.target.value);
      }
    }

    return (
      <Input
        ref={ref}
        value={
          value !== undefined
            ? maskPattern === 'cpf_cnpj'
              ? customMaskCpfCnpj(String(value))
              : mask(unmaskFn(String(value)), maskPattern, config)
            : internalValue
        }
        onChange={handleChange}
        onBlur={handleBlur}
        errorMessage={validationError}
        {...props}
      />
    );
  }
) as CompoundedMaskedInput;

  MaskedInput.mask = mask;
  MaskedInput.unmask = ((str: any, preserveChars?: string[]): string => {
    if (!preserveChars && typeof str === 'string' && /[A-Z]/i.test(str)) {
      return String(str).replace(/[^0-9A-Z]/gi, '').toUpperCase();
    }
    return unmask(str, preserveChars);
  }) as any;

export default MaskedInput;
